import React, { useContext } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import CropCard from "app/farmer360/KhetEdge/components/crop/CropCard";
import {
  COLORS,
  COMMON_STYLES,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import {
  AdvisoryQueryType,
  CropDataPoint,
  QueryName,
  SowingDataPoint,
  SubScribedCrops,
} from "app/farmer360/KhetEdge/models/crop";
import { ROUTES as KHET_EDGE_ROUTES } from "app/farmer360/KhetEdge/routes/types";
import { rnNavigation } from "app/utils/appConfig";
import { ResponsiveContext } from "app/context/ResponsiveUIContext";
import PlusIcon from "../icons/PlusIcon";
import { useTranslation } from "react-i18next";

const RenderItem = ({ item }: { item: SubScribedCrops }) => {
  const { id, data_points, status, is_active } = item;
  const crops: CropDataPoint | undefined = data_points.find(
    (i) => i.query_type === AdvisoryQueryType.CROP
  ) as CropDataPoint;
  const sowing_date: SowingDataPoint | undefined = data_points.find(
    (i) => i.query_type === AdvisoryQueryType.SOWING_DATE
  ) as SowingDataPoint;
  const stage: SowingDataPoint | undefined = data_points.find(
    (i) => i.query_type === AdvisoryQueryType.STAGE
  ) as SowingDataPoint;
  return (
    <CropCard
      key={id}
      cropName={crops?.value?.name || ""}
      cropImageUrl={crops?.value?.image || ""}
      sowingDate={sowing_date?.value || ""}
      transplantedDate={
        sowing_date?.query_name === QueryName.TransplantedDate
          ? sowing_date?.value
          : ""
      }
      stage={stage?.value || ""}
      status={status}
      statusStyles={{
        backgroundColor: is_active ? COLORS.primary100 : COLORS.neutral70,
      }}
      onClick={() => {
        if (is_active)
          rnNavigation({
            name: KHET_EDGE_ROUTES.ADVISORY,
            params: { advisoryId: item.id },
          });
      }}
    />
  );
};

const AddCropButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Pressable onPress={onClick} style={styles.addCropBtn}>
      <PlusIcon width={20} height={20} />
      <Text
        style={[
          TYPOGRAPHY.TEXT2,
          TEXT_STYLES.textPrimary100,
          COMMON_STYLES["ml1/4"],
          VARIANTS_STYLES.SEMI_EMPHASIZED,
        ]}
      >
        {t("add_crop")}
      </Text>
    </Pressable>
  );
};

const Crops = ({
  handleAddCrop,
  loading,
  crops,
}: {
  handleAddCrop: () => void;
  loading: boolean;
  crops: SubScribedCrops[];
}) => {
  const { t } = useTranslation();

  const { mediaStyles } = useContext(ResponsiveContext);
  return (
    <View style={styles.container}>
      <View style={styles.heading}>
        <Text
          style={[
            TYPOGRAPHY.HEADING3,
            VARIANTS_STYLES.MEDIUM,
            COMMON_STYLES["ml1"],
          ]}
        >
          {t("on_going_crops")}
        </Text>
        <AddCropButton onClick={() => handleAddCrop?.()} />
      </View>
      <ScrollView style={[COMMON_STYLES.ph1, COMMON_STYLES.pb2]}>
        {loading ? <Spinner /> : null}
        {crops?.length > 0 ? (
          <View
            style={mediaStyles({
              mobileStyle: [styles.contentMobile],
              tabletStyle: [styles.contentMobile],
              desktopStyle: [styles.content],
            })}
          >
            {crops.map((crop, idx) => (
              <RenderItem item={crop} key={`${crop.id}.${idx}`} />
            ))}
          </View>
        ) : loading ? null : (
          <Text style={styles.emptyComponent}>{t("no_crops_found_des")}</Text>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: "scroll",
  },
  content: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  contentMobile: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyComponent: {
    ...VARIANTS_STYLES.MEDIUM,
    ...TYPOGRAPHY.TEXT2,
    color: COLORS.neutral80,
    textAlign: "center",
    ...COMMON_STYLES.pt1,
  },
  addCropBtn: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: COLORS.neutral20,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    width: 110,
  },
  heading: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    ...COMMON_STYLES.mb1,
    ...COMMON_STYLES.ph1,
    ...COMMON_STYLES.pt1,
  },
});

export default Crops;
